<template>
  <div class="calendar">
    <div class="calendar-month">
      <b-button
        class="calendar-month__button"
        aria-label="select previous calendar month"
        type="button"
        :disabled="!canGoBack"
        @click="addMonth(-1)">
        <span class="visually-hidden">Go to previous month</span>
        <font-awesome-icon icon="chevron-left"></font-awesome-icon>
      </b-button>

      <div class="calendar-month__name">
        {{ currentSelectedMonthText }}
      </div>

      <b-button
        class="calendar-month__button"
        aria-label="select next calendar month"
        :disabled="!canGoForward"
        type="button"
        @click="addMonth(1)">
        <font-awesome-icon icon="chevron-right"></font-awesome-icon>
        <span class="visually-hidden">Go to next month</span>
      </b-button>
    </div>

    <div class="calendar-header">
      <div class="header-item">
        Mon
      </div>
      <div class="header-item">
        Tue
      </div>
      <div class="header-item">
        Wed
      </div>
      <div class="header-item">
        Thu
      </div>
      <div class="header-item">
        Fri
      </div>
      <div class="header-item">
        Sat
      </div>
      <div class="header-item">
        Sun
      </div>
    </div>

    <div class="calendar-body">
      <div v-if="loading"
           class="loading">
        <font-awesome-icon icon="spinner"
                           class="loading-spinner"
                           spin></font-awesome-icon>
      </div>

      <div v-for="(item, index) in setCalendarData"
           :key="index"
           class="calendar-item-wrapper">
        <template v-if="item != null">
          <div class="calendar-item-inner">
            <button
              class="calendar-item-button"
              aria-label="Set date"
              @click="calendarItemClicked(item.day)">
              <span class="visually-hidden">
                {{ `Select the ${item.dayWithSuffix} of ${currentSelectedMonthText}` }}
              </span>
              <CalendarItem
                :day="item.day"
                :odd="isOdd(index)"
                :month="getMonth"
                :selected="isSelected(item.day)"
                :data="getData(item.day)">
              </CalendarItem>
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getISODay, getDaysInMonth, startOfMonth, addMonths, isSameDay
} from "date-fns";
import axios from "axios";
import CalendarItem from "~/Components/Events/CalendarItem.vue";

export default {
  name: "CalendarComponent",
  components: {
    CalendarItem
  },
  props: {
    selectedDate: {
      type: Date,
      default: null
    },
    selectedMonth: {
      type: Date,
      default: null
    },
    selectedTypes: {
      type: Array,
      default: () => []
    },
    selectedSubTheme: {
      type: String,
      default: null
    },
    data: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      internalMonth: null,
      internalDate: null,
      mutable: {},
      loading: false
    };
  },
  computed: {
    canGoBack () {
      let result = false;
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth();
      const currentDate = new Date( currentYear, currentMonth );

      if ( this.internalMonth > currentDate ) {
        result = true;
      }

      return result;
    },

    canGoForward () {
      let result = true;

      if ( this.maxDate ) {
        const maxDate = new Date( Date.parse( this.maxDate ) );

        if ( this.internalMonth >= maxDate ) {
          result = false;
        }
      }

      return result;
    },

    currentSelectedMonthText () {
      return this.internalMonth.toLocaleString( "en-GB", {
        month: "long",
        year: "numeric"
      } );
    },

    /**
     * Method used to get the month value.
     */
    getMonth () {
      return parseInt( this.internalMonth.getMonth(), 10 );
    },

    maxDate () {
      let result = null;

      if ( this.mutable && Object.keys( this.mutable ).length > 0 ) {
        const dates = Object.keys( this.mutable );
        result = dates.reduce( ( acc, cv ) => ( acc > cv ? acc : cv ) );
      }

      return result;
    },

    /**
     * Method used to set calendar data based on the selected month.
     */
    setCalendarData () {
      const data = [];

      const date = startOfMonth( this.internalMonth );
      const start = getISODay( date );
      const monthLength = getDaysInMonth( date );

      // Pad the first few entries with nulls, based on when the first day of
      // the week starts.
      for ( let i = 0; i < start - 1; i += 1 ) {
        data.push( null );
      }

      // Fill the month data with day values, along with the current month.
      for ( let i = 0; i < monthLength; i += 1 ) {
        const dayData = {
          day: i + 1,
          month: parseInt( date.getMonth(), 10 ) + 1,
          data: null
        };
        data.push( dayData );
      }

      // Add the remaining days of the month to fill out the last
      // days of the last week.
      const remaining = 7 - ( data.length % 7 );
      if ( remaining < 7 ) {
        for ( let i = 0; i < remaining; i += 1 ) {
          data.push( null );
        }
      }

      // append additional data for ui
      const mappedData = data.map( d => ( d
        ? {
          ...d,
          dayWithSuffix: this.appendSuffixToDay( d.day )
        }
        : null )
      );
      return mappedData;
    }
  },
  watch: {
    selectedDate: {
      immediate: true,
      handler ( val ) {
        this.internalDate = val;
      }
    },
    internalDate ( val ) {
      this.$emit( "select-date", val );
    },
    selectedMonth: {
      immediate: true,
      handler ( val ) {
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth();
        let result = new Date( currentYear, currentMonth );

        if ( val ) {
          result = val;
        }

        this.internalMonth = result;
      }
    },
    selectedTypes: {
      deep: true,
      handler () {
        this.fetchData();
      }
    },
    selectedSubTheme () {
      this.fetchData();
    },
    internalMonth ( val ) {
      if ( this.selectedMonth ) {
        this.$emit( "select-month", val );
      }
    },
    /**
     * Watches the data prop, will set the mutable data field on change.
     */
    data: {
      immediate: true,
      handler ( val ) {
        if ( val === null || Object.entries( val ).length > 0 ) {
          this.loading = false;
        }

        if ( val === null || Object.entries( val ).length === 0 ) {
          this.mutable = {};
        } else {
          this.mutable = val;
        }
      }
    }
  },
  created () {
    this.fetchData();
  },
  methods: {
    appendSuffixToDay ( d ) {
      const day = Number.parseInt( d, 10 );
      let dateSuffix;

      if ( day === "01" || day === 1 || day === 21 || day === 31 ) {
        dateSuffix = "st";
      } else if ( day === 2 || day === 22 ) {
        dateSuffix = "nd";
      } else if ( day === 3 || day === 23 ) {
        dateSuffix = "rd";
      } else {
        dateSuffix = "th";
      }

      return `${d}${dateSuffix}`;
    },
    fetchData () {
      this.loading = true;
      axios
        .post( "/umbraco/Surface/eventSearch/Count", this.selectedTypes, {
          params: {
            selectedSubTheme: this.selectedSubTheme
          }
        } )
        .then( ( { data } ) => {
          this.mutable = data.dates;
        } )
        .finally( () => {
          this.loading = false;
        } );
    },
    isOdd ( index ) {
      return Math.floor( ( index / 7 ) % 2 ) === 0;
    },
    isSelected ( day ) {
      let result = false;

      const currentYear = new Date( this.internalMonth ).getFullYear();
      const currentMonth = new Date( this.internalMonth ).getMonth();
      const date = new Date( currentYear, currentMonth, day );

      if ( isSameDay( this.internalDate, date ) ) {
        result = true;
      }

      return result;
    },
    getData ( day ) {
      const currentYear = new Date( this.internalMonth ).getFullYear();
      const currentMonth = new Date( this.internalMonth ).getMonth() + 1;
      const paddedMonth = `${currentMonth}`.padStart( 2, "0" );
      const yearMonth = `${currentYear}-${paddedMonth}-`;
      const paddedDay = `${day}`.padStart( 2, "0" );

      const key = `${yearMonth + paddedDay}T00:00:00`;
      return this.mutable[key];
    },
    /**
     * Called when a calendar item is clicked.
     * @param {string} day The ID of the day that was clicked.
     */
    calendarItemClicked ( day ) {
      const currentYear = new Date( this.internalMonth ).getFullYear();
      const currentMonth = new Date( this.internalMonth ).getMonth();
      const date = new Date( currentYear, currentMonth, day );

      const hasData = this.getData( day );

      if ( hasData && ( !this.internalDate || !isSameDay( this.internalDate, date ) ) ) {
        this.internalDate = date;
      } else {
        this.internalDate = null;
      }
    },
    addMonth ( val ) {
      this.internalMonth = addMonths( this.internalMonth, val );
    }
  }
};
</script>

<style lang="less" scoped>
@border-radius: 0;
@background-color: #fff;

.loading {
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 100;
  border-radius: @border-radius;
  color: #017c7f;

  .loading-spinner {
    font-size: 4rem;
  }
}

.calendar {
  .calendar-month {
    display: flex;
    justify-content: space-between;
    background: #017c7f;
    color: #fff;
    font-weight: 600;
    align-items: stretch;
    border-right: 1px solid #fff;

    &__name {
      padding: 5px;
    }

    &__button {
      background: none;
      border: 0;
      color: #fff;
      transition: background-color 0.2s;
      padding: 0 10px;

      &:hover {
        background: darken(#017c7f, 10%);
      }

      &[disabled] {
        opacity: 0.6;
        pointer-events: none;
      }
    }
  }

  .calendar-header {
    display: flex;
    justify-content: space-evenly;
    background: #fff;

    .header-item {
      border: 1px solid #fff;
      border-left: 0;
      background: #757575;
      flex-grow: 1;
      text-align: center;
      flex-basis: 100%;
      color: #fff;
      padding: 5px;

      &:first-child {
        border-left: 0;
      }
    }
  }

  .calendar-body {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-evenly;
    padding: 1px;
    background: @background-color;
    border-radius: @border-radius;

    .calendar-item-wrapper {
      height: 100%;
      border-right: none;
      border-bottom: none;
      overflow: hidden;
      flex-grow: 1;
      flex-basis: calc((100% / 7) - 1px);

      &:not(:nth-child(7n)) {
        margin-right: 1px;
      }

      &:not(:nth-last-child(-n + 7)) {
        margin-bottom: 1px;
      }

      @supports (-ms-ime-align: auto) {
        flex-basis: calc((100% / 7) - 1px);
      }

      .calendar-item-inner {
        width: 100%;
        height: 0;
        padding-bottom: 66.66%;
        position: relative;

        .calendar-item {
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 1;
        }

        .calendar-item-button {
          padding: 0;
          border: 0;
          background: none;
          cursor: default;
        }

        @media (max-width: 991px) {
          padding-bottom: 100%;
        }
      }

      &:first-child {
        border-top-left-radius: @border-radius;
      }

      &:nth-child(7) {
        border-top-right-radius: @border-radius;
      }

      &:nth-child(7n) {
        margin-right: 0px;
      }

      &:nth-last-child(7) {
        border-bottom-left-radius: @border-radius;
      }

      @iterations: 7;
      .item-loop (@i) when (@i > 0) {
        &:nth-last-child(@{i}) {
          margin-bottom: 0;
        }
        .item-loop(@i - 1);
      }

      .item-loop(@iterations);

      &:last-child {
        border-bottom-right-radius: @border-radius;
      }
    }
  }
}
</style>
